import { getBuildingClassification } from '../constants/projectFieldsConfig';
import {
  DEFAULT_PROJECT_CATEGORY,
  getProjectCategoryProfile,
  isCivilCategory,
  normalizeProjectCategory,
} from '../constants/projectCategoryProfiles';
import {
  getInfraFamily,
  getInfraMetricsProfile,
  infraByKey,
  resolveLegacyInfraTypeKey,
} from '../constants/infrastructureElementTypes';

export { getInfraMetricsProfile };

export function newEntityId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ent_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyEntity(partial = {}, profile) {
  const kind = partial.structureKind || profile?.structureKindDefault || 'building';
  return {
    id: partial.id || newEntityId(),
    name: partial.name || 'Primary Building',
    sortOrder: partial.sortOrder ?? 0,
    structureKind: kind,
    occupancyTypes: partial.occupancyTypes || [],
    occupancyCodes: partial.occupancyCodes || [],
    majorOccupancy: partial.majorOccupancy || '',
    buildingClassification: partial.buildingClassification || '',
    constructionType: partial.constructionType || '',
    storeysAboveGrade: partial.storeysAboveGrade ?? '',
    storeysBelowGrade: partial.storeysBelowGrade ?? '',
    buildingFootprintArea: partial.buildingFootprintArea ?? '',
    buildingFootprintAreaExisting: partial.buildingFootprintAreaExisting ?? '',
    buildingFootprintAreaProposed: partial.buildingFootprintAreaProposed ?? '',
    grossFloorAreaExisting: partial.grossFloorAreaExisting ?? '',
    grossFloorAreaProposed: partial.grossFloorAreaProposed ?? '',
    grossFloorArea: partial.grossFloorArea ?? '',
    buildingHeight: partial.buildingHeight ?? '',
    adjacentStreets: partial.adjacentStreets ?? '',
    averageGrade: partial.averageGrade ?? '',
    highBuilding: partial.highBuilding ?? false,
    sprinklerSystem: partial.sprinklerSystem || '',
    fireAlarm: partial.fireAlarm || '',
    standpipe: partial.standpipe || '',
    hazardousSubstances: partial.hazardousSubstances ?? false,
    infraType: partial.infraType || '',
    civilVerticals: partial.civilVerticals || [],
    scopeOfWork: partial.scopeOfWork || '',
    infraTypes: partial.infraTypes || [],
    infraTypeCodes: partial.infraTypeCodes || [],
    primaryInfraType: partial.primaryInfraType || '',
    infraFamily: partial.infraFamily || '',
    scope: partial.scope || '',
    designStandard: partial.designStandard || '',
    linearExtent: partial.linearExtent ?? '',
    area: partial.area ?? '',
    rowWidth: partial.rowWidth ?? '',
    networkDiameter: partial.networkDiameter ?? '',
    burialDepth: partial.burialDepth ?? '',
    materialContext: partial.materialContext || '',
    environmentalFloodplain: partial.environmentalFloodplain || 'unknown',
    environmentalSwm: partial.environmentalSwm || 'unknown',
    disciplines: partial.disciplines || [],
  };
}

export function normalizeLegacyInfraEntity(ent, occupancyRegion = 'CA') {
  if (!ent) return ent;
  const region = occupancyRegion === 'US' ? 'US' : 'CA';
  let next = { ...ent };
  if ((!next.infraTypes || next.infraTypes.length === 0) && next.infraType) {
    const key = resolveLegacyInfraTypeKey(next.infraType);
    next = {
      ...next,
      infraTypes: [key],
      primaryInfraType: key,
      infraTypeCodes: [infraByKey(key, region)?.code || key],
    };
  }
  if (next.infraTypes?.length && !next.primaryInfraType) {
    next.primaryInfraType = next.infraTypes[0];
  }
  if (next.primaryInfraType && !next.infraFamily) {
    next.infraFamily = getInfraFamily(next.primaryInfraType);
  }
  if (next.infraTypes?.length && (!next.infraTypeCodes || next.infraTypeCodes.length === 0)) {
    next.infraTypeCodes = next.infraTypes.map((k) => infraByKey(k, region)?.code || k);
  }
  if (next.primaryInfraType && !next.infraType) {
    next.infraType = infraByKey(next.primaryInfraType, region)?.title || next.primaryInfraType;
  }
  return next;
}

function parseEntitiesData(raw) {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Load category + entities from Project (entitiesData when schema live, else description payload or flat fields). */
export function loadProjectEntities(project) {
  const fromDesc = parseEntityPayloadFromDescription(project?.description);
  const projectCategory = normalizeProjectCategory(
    project?.projectCategory || fromDesc?.projectCategory
  );
  const profile = getProjectCategoryProfile(projectCategory);
  const fromJson = parseEntitiesData(project?.entitiesData) || parseEntitiesData(fromDesc?.entitiesData);

  if (fromJson?.length) {
    const region = project?.occupancyRegion === 'US' ? 'US' : 'CA';
    return {
      projectCategory,
      entities: fromJson.map((e, i) => {
        const normalized = isCivilCategory(projectCategory)
          ? normalizeLegacyInfraEntity(e, region)
          : e;
        return createEmptyEntity({ ...normalized, sortOrder: normalized.sortOrder ?? i }, profile);
      }),
    };
  }

  return {
    projectCategory,
    entities: [entityFromFlatProject(project, profile)],
  };
}

export function entityFromFlatProject(project, profile) {
  const p = project || {};
  return createEmptyEntity(
    {
      id: newEntityId(),
      name: p.name ? `${p.name} — Primary` : 'Primary Building',
      sortOrder: 0,
      occupancyTypes: p.occupancyTypes || [],
      occupancyCodes: p.occupancyCodes || [],
      majorOccupancy: p.majorOccupancy || '',
      buildingClassification: p.buildingClassification || '',
      constructionType: p.constructionType || '',
      storeysAboveGrade: p.storeysAboveGrade ?? '',
      storeysBelowGrade: p.storeysBelowGrade ?? '',
      buildingFootprintArea: p.buildingFootprintArea ?? '',
      grossFloorArea: p.grossFloorArea ?? '',
      buildingHeight: p.buildingHeight ?? '',
      adjacentStreets: p.adjacentStreets ?? '',
      averageGrade: p.averageGrade ?? '',
      highBuilding: p.highBuilding ?? false,
      sprinklerSystem: p.sprinklerSystem || '',
      fireAlarm: p.fireAlarm || '',
      standpipe: p.standpipe || '',
      hazardousSubstances: p.hazardousSubstances ?? false,
    },
    profile
  );
}

/** Snapshot current form state into an entity object. */
export function captureEntityFromFormState(state) {
  const {
    entityMeta = {},
    occupancyTypes,
    occupancyCodes,
    majorOccupancy,
    classificationOverride,
    occupancyRegion,
    storeysAboveGrade,
    buildingFootprintArea,
    grossFloorArea,
    storeysBelowGrade,
    averageGrade,
    adjacentStreets,
    buildingHeight,
    constructionType,
    highBuilding,
    sprinklerSystem,
    fireAlarm,
    standpipe,
    hazardousSubstances,
    buildingFootprintAreaExisting,
    buildingFootprintAreaProposed,
    grossFloorAreaExisting,
    grossFloorAreaProposed,
    isAreaSplit,
    infraTypes,
    infraTypeCodes,
    primaryInfraType,
    scope,
    designStandard,
    linearExtent,
    area,
    rowWidth,
    networkDiameter,
    burialDepth,
    materialContext,
    environmentalFloodplain,
    environmentalSwm,
  } = state;

  const resolvedFootprint = isAreaSplit
    ? ((parseFloat(buildingFootprintAreaExisting || '') || 0) + (parseFloat(buildingFootprintAreaProposed || '') || 0)) || ''
    : buildingFootprintArea;

  const resolvedGfa = isAreaSplit
    ? ((parseFloat(grossFloorAreaExisting || '') || 0) + (parseFloat(grossFloorAreaProposed || '') || 0)) || ''
    : grossFloorArea;

  const autoClass = getBuildingClassification({
    occupancyRegion,
    storeysAboveGrade,
    buildingFootprintArea: resolvedFootprint,
    occupancyCodes,
  });

  const region = occupancyRegion === 'US' ? 'US' : 'CA';
  const resolvedPrimary = primaryInfraType || entityMeta?.primaryInfraType || '';
  const resolvedTypes = infraTypes ?? entityMeta?.infraTypes ?? [];
  const resolvedFamily = resolvedPrimary ? getInfraFamily(resolvedPrimary) : (entityMeta?.infraFamily || '');

  return createEmptyEntity(
    {
      ...entityMeta,
      occupancyTypes,
      occupancyCodes,
      majorOccupancy,
      buildingClassification: classificationOverride || autoClass,
      constructionType,
      storeysAboveGrade,
      storeysBelowGrade,
      buildingFootprintArea: resolvedFootprint,
      grossFloorArea: resolvedGfa,
      buildingFootprintAreaExisting,
      buildingFootprintAreaProposed,
      grossFloorAreaExisting,
      grossFloorAreaProposed,
      buildingHeight,
      adjacentStreets,
      averageGrade,
      highBuilding,
      sprinklerSystem,
      fireAlarm,
      standpipe,
      hazardousSubstances,
      infraTypes: resolvedTypes,
      infraTypeCodes: infraTypeCodes ?? entityMeta?.infraTypeCodes ?? [],
      primaryInfraType: resolvedPrimary,
      infraFamily: resolvedFamily,
      scope: scope ?? entityMeta?.scope ?? '',
      designStandard: designStandard ?? entityMeta?.designStandard ?? '',
      linearExtent: linearExtent ?? entityMeta?.linearExtent ?? '',
      area: area ?? entityMeta?.area ?? '',
      rowWidth: rowWidth ?? entityMeta?.rowWidth ?? '',
      networkDiameter: networkDiameter ?? entityMeta?.networkDiameter ?? '',
      burialDepth: burialDepth ?? entityMeta?.burialDepth ?? '',
      materialContext: materialContext ?? entityMeta?.materialContext ?? '',
      environmentalFloodplain: environmentalFloodplain ?? entityMeta?.environmentalFloodplain ?? 'unknown',
      environmentalSwm: environmentalSwm ?? entityMeta?.environmentalSwm ?? 'unknown',
      infraType: resolvedPrimary
        ? (infraByKey(resolvedPrimary, region)?.title || entityMeta?.infraType || '')
        : (entityMeta?.infraType || ''),
    },
    getProjectCategoryProfile(state.projectCategory)
  );
}

/** Apply entity fields to form state setters (returns patch object for setState). */
export function entityToFormPatch(entity) {
  const e = entity || {};
  return {
    entityMeta: {
      id: e.id,
      name: e.name,
      sortOrder: e.sortOrder,
      structureKind: e.structureKind,
      infraType: e.infraType,
      civilVerticals: e.civilVerticals || [],
      scopeOfWork: e.scopeOfWork || '',
      infraTypes: e.infraTypes || [],
      infraTypeCodes: e.infraTypeCodes || [],
      primaryInfraType: e.primaryInfraType || '',
      infraFamily: e.infraFamily || '',
      scope: e.scope,
      designStandard: e.designStandard || '',
      linearExtent: e.linearExtent,
      area: e.area,
      rowWidth: e.rowWidth,
      networkDiameter: e.networkDiameter,
      burialDepth: e.burialDepth,
      materialContext: e.materialContext || '',
      environmentalFloodplain: e.environmentalFloodplain || 'unknown',
      environmentalSwm: e.environmentalSwm || 'unknown',
      disciplines: e.disciplines || [],
    },
    occupancyTypes: e.occupancyTypes || [],
    occupancyCodes: e.occupancyCodes || [],
    majorOccupancy: e.majorOccupancy || '',
    storeysAboveGrade: e.storeysAboveGrade ?? '',
    storeysBelowGrade: e.storeysBelowGrade ?? '',
    buildingFootprintArea: e.buildingFootprintArea ?? '',
    buildingFootprintAreaExisting:
      e.buildingFootprintAreaExisting ??
      (e.buildingFootprintAreaProposed == null && e.buildingFootprintArea ? String(e.buildingFootprintArea) : ''),
    buildingFootprintAreaProposed: e.buildingFootprintAreaProposed ?? '',
    grossFloorAreaExisting:
      e.grossFloorAreaExisting ??
      (e.grossFloorAreaProposed == null && e.grossFloorArea ? String(e.grossFloorArea) : ''),
    grossFloorAreaProposed: e.grossFloorAreaProposed ?? '',
    grossFloorArea: e.grossFloorArea ?? '',
    averageGrade: e.averageGrade ?? '',
    adjacentStreets: e.adjacentStreets ?? '',
    buildingHeight: e.buildingHeight ?? '',
    constructionType: e.constructionType || '',
    highBuilding: e.highBuilding ?? false,
    sprinklerSystem: e.sprinklerSystem || '',
    fireAlarm: e.fireAlarm || '',
    standpipe: e.standpipe || '',
    hazardousSubstances: e.hazardousSubstances ?? false,
    classificationOverride:
      e.buildingClassification &&
      e.buildingClassification !== getBuildingClassification({
        occupancyRegion: 'CA',
        storeysAboveGrade: e.storeysAboveGrade,
        buildingFootprintArea: e.buildingFootprintArea,
        occupancyCodes: e.occupancyCodes,
      })
        ? e.buildingClassification
        : null,
  };
}

/** Mirror primary entity onto flat Project fields (required for Classifier, compliance, chatbot). */
export function entityToFlatProjectMirror(entity, occupancyRegion) {
  const e = entity || {};
  const autoClass = getBuildingClassification({
    occupancyRegion,
    storeysAboveGrade: e.storeysAboveGrade,
    buildingFootprintArea: e.buildingFootprintArea,
    occupancyCodes: e.occupancyCodes,
  });
  const classification = e.buildingClassification || autoClass;

  return {
    occupancyTypes: e.occupancyTypes || [],
    occupancyCodes: e.occupancyCodes || [],
    majorOccupancy: e.majorOccupancy || null,
    buildingClassification: classification || '',
    constructionType: e.constructionType || null,
    storeysAboveGrade: e.storeysAboveGrade !== '' && e.storeysAboveGrade != null ? parseInt(e.storeysAboveGrade, 10) : null,
    storeysBelowGrade: e.storeysBelowGrade !== '' && e.storeysBelowGrade != null ? parseInt(e.storeysBelowGrade, 10) : null,
    buildingFootprintArea: e.buildingFootprintArea !== '' && e.buildingFootprintArea != null ? parseFloat(e.buildingFootprintArea) : null,
    buildingFootprintAreaExisting: e.buildingFootprintAreaExisting !== '' && e.buildingFootprintAreaExisting != null ? parseFloat(e.buildingFootprintAreaExisting) : null,
    buildingFootprintAreaProposed: e.buildingFootprintAreaProposed !== '' && e.buildingFootprintAreaProposed != null ? parseFloat(e.buildingFootprintAreaProposed) : null,
    grossFloorAreaExisting: e.grossFloorAreaExisting !== '' && e.grossFloorAreaExisting != null ? parseFloat(e.grossFloorAreaExisting) : null,
    grossFloorAreaProposed: e.grossFloorAreaProposed !== '' && e.grossFloorAreaProposed != null ? parseFloat(e.grossFloorAreaProposed) : null,
    grossFloorArea: e.grossFloorArea !== '' && e.grossFloorArea != null ? parseFloat(e.grossFloorArea) : null,
    buildingHeight: e.buildingHeight !== '' && e.buildingHeight != null ? parseFloat(e.buildingHeight) : null,
    adjacentStreets: e.adjacentStreets !== '' && e.adjacentStreets != null ? parseInt(e.adjacentStreets, 10) : null,
    averageGrade: e.averageGrade !== '' && e.averageGrade != null ? parseFloat(e.averageGrade) : null,
    highBuilding: !!e.highBuilding,
    sprinklerSystem: e.sprinklerSystem || null,
    fireAlarm: e.fireAlarm || null,
    standpipe: e.standpipe || null,
    hazardousSubstances: !!e.hazardousSubstances,
  };
}

export function defaultEntitiesForCategory(category) {
  const profile = getProjectCategoryProfile(category);
  return (profile.defaultEntities || [{ name: 'Primary Building' }]).map((d, i) =>
    createEmptyEntity({ ...d, sortOrder: i }, profile)
  );
}

/** Wizard starts with one entity; user adds more via "+ Add Additional …". */
export function wizardDefaultEntitiesForCategory(category) {
  const profile = getProjectCategoryProfile(category);
  const kind = profile.structureKindDefault || 'building';
  let name = 'Primary Building';
  if (kind === 'bridge') name = 'Primary Structure';
  else if (kind === 'other') name = 'Primary Element';
  else if (kind === 'site') name = 'Civil Scope';
  return [createEmptyEntity({ name, sortOrder: 0, structureKind: kind }, profile)];
}

export function serializeEntitiesData(entities) {
  return JSON.stringify(entities || []);
}

/** Hidden payload in description when AppSync schema lacks entitiesData / projectCategory. */
export const ENTITY_PAYLOAD_MARKER = '\n<!--codiii-entity-payload:v1-->';

export function stripEntityPayloadFromDescription(description) {
  if (!description) return '';
  const idx = description.indexOf(ENTITY_PAYLOAD_MARKER);
  return idx >= 0 ? description.slice(0, idx).trimEnd() : description;
}

export function embedEntityPayloadInDescription(description, { projectCategory, entitiesData, documentTags }) {
  const base = stripEntityPayloadFromDescription(description || '');
  const hasTags = documentTags && Object.keys(documentTags).length > 0;
  if (!projectCategory && !entitiesData && !hasTags) return base;
  const payload = JSON.stringify({
    projectCategory: projectCategory || undefined,
    entitiesData: entitiesData || undefined,
    documentTags: hasTags ? documentTags : undefined,
  });
  return `${base}${ENTITY_PAYLOAD_MARKER}${payload}`;
}

export function parseDocumentTagsFromProject(project) {
  const payload = parseEntityPayloadFromDescription(project?.description);
  const tags = payload?.documentTags;
  return tags && typeof tags === 'object' ? tags : {};
}

export function descriptionHasEntityPayload(description) {
  return !!(description && description.includes(ENTITY_PAYLOAD_MARKER));
}

/** True when entity data lives only in description (pre-schema or fallback era). */
export function needsEntityPayloadMigration(project) {
  if (!descriptionHasEntityPayload(project?.description)) return false;
  if (project?.projectCategory) return false;
  if (parseEntitiesData(project?.entitiesData)?.length) return false;
  return !!parseEntityPayloadFromDescription(project?.description);
}

export function extractEntityPayloadForMigration(project) {
  const payload = parseEntityPayloadFromDescription(project?.description);
  if (!payload) return null;
  return {
    cleanDescription: stripEntityPayloadFromDescription(project?.description || ''),
    projectCategory: payload.projectCategory || null,
    entitiesData: payload.entitiesData || null,
    documentTags: payload.documentTags && typeof payload.documentTags === 'object' ? payload.documentTags : {},
  };
}

export function parseEntityPayloadFromDescription(description) {
  if (!description) return null;
  const idx = description.indexOf(ENTITY_PAYLOAD_MARKER);
  if (idx < 0) return null;
  try {
    return JSON.parse(description.slice(idx + ENTITY_PAYLOAD_MARKER.length));
  } catch {
    return null;
  }
}
